import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Spinner } from '@/components/admin/ui';
import {
  EmptyState,
} from '@/components';
import type { DictionaryBrowseEntry } from '@/lib/admin-types';
import { apiJson } from '@/lib/api';
import { DictionaryEntryEditor } from './-dictionary-shared';

export const Route = createFileRoute('/admin/dictionary/')({
  component: DictionaryReviewPage,
});

function DictionaryReviewPage() {
  const [queueIndex, setQueueIndex] = useState(0);

  const entryQ = useQuery({
    queryKey: ['admin', 'dictionary', 'review-queue', queueIndex],
    queryFn: () =>
      apiJson<DictionaryBrowseEntry>(
        `/v1/admin/dictionary/review-queue?queue_index=${queueIndex}`
      ),
    placeholderData: (prev) => prev,
  });

  const entry = entryQ.data;
  const queueTotal = entry?.queue_total ?? 0;

  const go = useCallback(
    (next: number) => {
      if (queueTotal > 0) {
        setQueueIndex(Math.max(0, Math.min(next, queueTotal - 1)));
      } else {
        setQueueIndex(Math.max(0, next));
      }
    },
    [queueTotal]
  );

  if (entryQ.isLoading && !entry) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (entryQ.isError) {
    const message = (entryQ.error as Error)?.message ?? '';
    if (message.includes('Review queue is empty')) {
      return (
        <EmptyState
          title="Review queue is empty"
          hint="All entries have been reviewed, or use Browse to find bookmarked terms."
        />
      );
    }
    return (
      <EmptyState title="Could not load review queue" hint={message} />
    );
  }

  if (!entry) {
    return <EmptyState title="No entries available" />;
  }

  return (
    <DictionaryEntryEditor
      entry={entry}
      entryQueryKey={['admin', 'dictionary', 'review-queue', queueIndex]}
      positionLabel={`Queue ${(entry.queue_index ?? queueIndex) + 1} of ${queueTotal}`}
      onPrev={() => go(queueIndex - 1)}
      onNext={() => go(queueIndex + 1)}
      disablePrev={queueIndex === 0}
      disableNext={queueTotal > 0 && queueIndex >= queueTotal - 1}
      jumpPlaceholder="Jump queue #"
      onJump={(oneBased) => go(oneBased - 1)}
      showReviewActions
      onReviewStatusApplied={() => {
        void entryQ.refetch();
      }}
    />
  );
}
